import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { toast } from "react-toastify";
import campaignService from "../services/campaignService";
import { useDashboardUI } from "../context/GlobalContext";
import { decodeAccessToken } from "../helpers/helpers";

ModuleRegistry.registerModules([AllCommunityModule]);

const BLOB_SAS_URL = process.env.REACT_APP_BLOB_SAS_URL;
const DEFAULT_CONTAINER = "campaign-files";

const TWILIO_SUCCESS_STATUSES = ["delivered", "sent", "success"];
const TWILIO_FAILED_STATUSES = [
  "undelivered",
  "failed",
  "canceled",
  "cancelled",
];

const CAMPAIGN_MODE_MAP = {
  sms: "SMS",
  ivr: "IVR",
  SMS: "SMS",
  IVR: "IVR",
};

// Custom cell renderer for Mode column (tag style)
const ModeCellRenderer = (props) => {
  if (!props.value) return null;
  return (
    <span className="inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full border bg-blue-50 text-blue-600 border-blue-200">
      {props.value}
    </span>
  );
};

// Custom cell renderer for Status column (tag style with color)
const StatusCellRenderer = (props) => {
  if (!props.value) return null;

  const getStyle = (status) => {
    switch (status) {
      case "Success":
      case "Delivered":
        return "bg-green-50 text-green-600 border-green-200";
      case "Failed":
        return "bg-red-50 text-red-600 border-red-200";
      case "Queued":
      default:
        return "bg-gray-100 text-gray-500 border-gray-200";
    }
  };

  const getDot = (status) => {
    switch (status) {
      case "Success":
      case "Delivered":
        return "bg-green-500";
      case "Failed":
        return "bg-red-500";
      case "Queued":
      default:
        return "bg-blue-400";
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full border ${getStyle(props.value)}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${getDot(props.value)}`} />
      {props.value}
    </span>
  );
};

function safeCsvSplit(row) {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    const nextChar = row[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current);
  return result;
}


const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const { selectedFacilityFilter } = useDashboardUI();

  const [campaignDetails, setCampaignDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const defaultColDef = {
    flex: 1,
    minWidth: 150,
    sortable: true,
    resizable: true,
  };

  const fetchFileFromBlob = useCallback(async (fileName, sasToken) => {
    // Build direct blob URL and fetch via browser fetch API
    const blobUrl = `${BLOB_SAS_URL}/${DEFAULT_CONTAINER}/${fileName}?${sasToken}`;
    const response = await fetch(blobUrl);
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }
    return await response.text();
  }, []);

  const prepareCSVData = useCallback(
    (csvData, campaign) => {
      const rows = csvData.split("\n");
      const headers = rows[0].replace(/\r/g, "").split(",");
      headers.push("campaign_mode");

      const data = [];
      for (let i = 1; i < rows.length; i++) {
        if (!rows[i].trim()) continue;
        const values = safeCsvSplit(rows[i]);
        const row = {};
        for (let j = 0; j < headers.length; j++) {
          if (headers[j] === "campaign_mode") {
            row[headers[j]] = CAMPAIGN_MODE_MAP[campaign.mode] || campaign.mode;
          } else {
            row[headers[j]] = values[j];
          }
        }
        if (row["unique_campaign_id"]) {
          row["unique_campaign_id"] = row["unique_campaign_id"].replace(/[\r\n]/g, "").trim();
        }
        data.push(row);
      }
      const excludeColumns = ["campaign_mode", "unique_campaign_id"];
      const colDefs = headers
        .filter((h) => !excludeColumns.includes(h))
        .map((header) => ({
          field: header,
          headerName: header,
          tooltipField: header,
          cellStyle: { textAlign: "left" },
          sortable: true,
        }));

      // colDefs.push({
      //   field: "campaign_mode",
      //   headerName: "Mode",
      //   cellRenderer: ModeCellRenderer,
      //   cellClass: "flex items-center justify-end",
      //   headerClass: "ag-right-aligned-header",
      // });

      colDefs.push({
        field: "status",
        headerName: "Status",
        cellRenderer: StatusCellRenderer,
        cellClass: "flex items-center justify-end",
        headerClass: "ag-right-aligned-header",
      });

      setRowData(data);
      setColumnDefs(colDefs);
    },
    []
  );

  const fetchStatuses = useCallback(
    async (campaign, gridApi) => {
      try {
        const res = await campaignService.getCampaignsData(campaign._id);
        const statusData = res.data || [];
        const statuses = {};
        
        statusData.forEach((x) => {
          const status = x?.twilio?.status.toLowerCase() || x?.status.toLowerCase();
          if (TWILIO_SUCCESS_STATUSES.includes(status)) {
            if(status == 'delivered') {
              statuses[x.unique_campaign_id] = "Delivered";
            } else {
              statuses[x.unique_campaign_id] = "Sent";
            }
          } else if (TWILIO_FAILED_STATUSES.includes(status)) {
            statuses[x.unique_campaign_id] = "Failed";
          }
        });

        if (gridApi) {
          gridApi.forEachNode((rowNode) => {
            const uid = rowNode.data?.unique_campaign_id;
            rowNode.setDataValue("status", statuses[uid] ? statuses[uid] : "Queued");
          });
          
        }
      } catch (err) {
        console.error("Error fetching campaign statuses:", err);
      }
    },
    []
  );

  useEffect(() => {
    if (!campaignId) {
      toast.error("Campaign not found");
      navigate("/campaigns");
      return;
    }

    const loadCampaignDetail = async () => {
      try {
        setIsLoading(true);

        // Fetch campaign details
        const facilityId = selectedFacilityFilter?.facility_number || decodeAccessToken()?.facility_id || "";
        const detailsRes = await campaignService.getCampaignDetails(campaignId, facilityId);
        const campaign = detailsRes.data;
        setCampaignDetails(campaign);

        // Fetch read SAS token & download file
        const sasToken = await campaignService.getReadSASToken();
        const csvData = await fetchFileFromBlob(
          campaign.blob_actual_file,
          sasToken
        );

        // Parse CSV and build grid data
        prepareCSVData(csvData, campaign);
        setIsLoading(false);

        setTimeout(() => {
          const gridApi = gridRef.current?.api;
          fetchStatuses(campaign, gridApi);
        }, 500);
      } catch (err) {
        console.error("Error loading campaign detail:", err);
        toast.error("Failed to load campaign details");
        navigate("/campaigns");
      }
    };

    loadCampaignDetail();
  }, [campaignId, navigate, fetchFileFromBlob, prepareCSVData, fetchStatuses]);

  const handleDownloadCSV = () => {
    gridRef.current?.api?.exportDataAsCsv();
  };

  return (
    <div className="w-full rounded-2xl bg-white p-6 flex flex-col h-[calc(100vh-120px)]">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <button
          onClick={() => navigate("/campaigns")}
          className="flex items-center gap-1 hover:text-gray-700 transition"
        >
          <ArrowLeft size={16} />
          Campaigns
        </button>
        <span>/</span>
        <span className="text-gray-800 font-medium">
          {campaignDetails?.name || "Recipients"}
        </span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h1 className="text-xl font-semibold text-gray-800">Recipients</h1>
        <div className="flex items-center gap-3">
          {!isLoading && (
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-full hover:bg-gray-50 transition"
            >
              <Download size={14} />
              Download File
            </button>
          )}
        </div>
      </div>

      {/* AG Grid */}
      <div className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2B3674] mr-3" />
            <span className="text-sm text-gray-500">
              Loading campaign data...
            </span>
          </div>
        ) : (
          <div className="ag-theme-alpine w-full h-full">
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              animateRows={false}
              enableBrowserTooltips={true}
              getRowId={(params) => params.data.id}
              suppressDragLeaveHidesColumns={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignDetail;
